/* @preserve */
/* @class */


/**
 * Modern Catastro Client voor Vite
 * Direct API calls met proxy ondersteuning
 */
export default class CatastroClient {
    constructor() {
        this.baseUrl = import.meta.env.VITE_API_BASE_URL || '/catastro'; // Vite proxy in dev, /catastro in productie
        this.timeout = 30000;
    }

    async makeRequest(endpoint, params = {}) {
        // Params omzetten naar querystring
        const queryString = params && Object.keys(params).length
            ? '?' + new URLSearchParams(params).toString()
            : '';
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);
        try {
            const response = await fetch(`${this.baseUrl}${endpoint}${queryString}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                },
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (error) {
            console.error('API Request Error:', error);
            if (error.name === 'AbortError') {
                throw new Error('Netwerk fout: Timeout van server');
            } else {
                throw new Error(`Request fout: ${error.message}`);
            }
        }
    }

    async getProvinces() {
        const result = await this.makeRequest('/COVCCallejeroCodigos.svc/json/ObtenerProvincias');
        return result.consulta_provincieroResult?.provinciero?.prov?.map(p => ({
            code: p.cpine,
            name: p.np
        })) || [];
    }

    async getMunicipalities(provinceCode) {
        const result = await this.makeRequest('/COVCCallejeroCodigos.svc/json/ObtenerMunicipiosCodigos', {
            CodigoProvincia: provinceCode
        });
        return result.consulta_municipieroResult?.municipiero?.muni?.map(m => ({
            code: m.locat?.cmc || m.cmun,
            name: m.nm
        })) || [];
    }

    async getStreets(provinceCode, municipalityCode) {
        const result = await this.makeRequest('/COVCCallejeroCodigos.svc/json/ObtenerCallejeroCodigos', {
            CodigoProvincia: provinceCode,
            CodigoMunicipio: municipalityCode
        });
        
        const callejeroResult = result.consulta_callejeroResult?.callejero?.calle;
        
        if (Array.isArray(callejeroResult)) {
            return callejeroResult.map(c => ({
                code: c.dir?.cv || 'UNK',
                name: c.dir?.nv || 'Sin nombre',
                type: c.dir?.tv || 'CL'
            }));
        } else if (callejeroResult) {
            return [{
                code: callejeroResult.dir?.cv || 'UNK',
                name: callejeroResult.dir?.nv || 'Sin nombre',
                type: callejeroResult.dir?.tv || 'CL'
            }];
        }
        
        return [];
    }

    async getPropertyData(codigoProvincia, codigoMunicipio, codigoVia, numero) {
        return await this.makeRequest('/COVCCallejeroCodigos.svc/json/Consulta_DNPLOC_Codigos', {
            CodigoProvincia: codigoProvincia,
            CodigoMunicipio: codigoMunicipio,
            CodigoVia: codigoVia,
            Numero: numero
        });
    }

    async getCodigoRegistralData(refCat) {
        return await this.makeRequest('/COVCCallejero.svc/json/Consulta_DNPRC', {
            RefCat: refCat
        });
    }

    async testConnection() {
        try {
            await this.getProvinces();
            return { success: true, message: 'Verbinding OK' };
        } catch (error) {
            return { success: false, message: error.message };
        }
    }
}

/* 
  DIT IS DE MAGISCHE FIX:
  Hiermee ziet Terser dat methods worden gebruikt
  → en durft hij ze niet meer weg te schudden.
*/
void CatastroClient.prototype.testConnection;
